import { useQuery } from "@tanstack/react-query";

import type { AgencySubscription, Pricing } from "common/types";

import { post } from "../helpers/HTTP";

async function customerEstimateUpdateRequest(
  planId: string,
  quantity: number,
  prorate: boolean,
): Promise<{
  amountDue: number;
  nextBillingAt: number;
  pricePerItem: number;
  basePrice: number;
}> {
  const { estimate } = await post("subscriptions/estimates/update", {
    item_price_id: planId,
    seat_count: quantity,
    prorate,
  });

  const { subscription_estimate: subscriptionEstimate } = estimate;

  const invoiceEstimate =
    estimate.invoice_estimate || estimate.next_invoice_estimate;

  const flatFeeItem = invoiceEstimate.line_items.find(
    ({ pricing_model }: { pricing_model: string }) =>
      pricing_model === "flat_fee",
  );

  return {
    amountDue: invoiceEstimate.sub_total,
    nextBillingAt: subscriptionEstimate.next_billing_at,
    pricePerItem: invoiceEstimate.line_items.find(
      ({ pricing_model }: { pricing_model: string }) =>
        pricing_model === "per_unit",
    ).unit_amount,
    //when there is no change, the flat fee item is not returned by chargebee, maybe we shouldn't call the request at all
    basePrice: flatFeeItem ? flatFeeItem.unit_amount : 0,
  };
}

export default function useCustomerEstimateUpdate({
  subscription,
  pricing,
  quantity,
  prorate,
}: {
  subscription?: AgencySubscription;
  pricing: { addon: Pricing; plan: Pricing };
  quantity: number;
  prorate: boolean;
}) {
  const { plan } = pricing;

  const {
    data: estimateUpdate,
    isFetching: isLoadingEstimateUpdate,
    error: estimateUpdateError,
  } = useQuery({
    queryKey: ["customerEstimateUpdate", pricing, quantity, prorate],
    queryFn: () => customerEstimateUpdateRequest(plan.id, quantity, prorate),
    enabled: !!subscription,
  });

  return { estimateUpdate, isLoadingEstimateUpdate, estimateUpdateError };
}
